import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchAdminAffiliates,
  fetchAdminPartners,
  fetchAdminPartnerDetail,
  promotePartner,
  updatePartnerTerms,
  generatePartnerAgreement,
  runPartnerAudit,
  recordPartnerPayout,
  deactivatePartner,
  fetchAdminPayoutRequests,
  approvePayoutRequest,
  rejectPayoutRequest,
  adminSearchUsers,
} from "@/lib/api";
import UserBadge from "@/components/UserBadge";
import AdminToggle from "@/components/AdminToggle";
import {
  ChevronLeft,
  Handshake,
  Loader2,
  Plus,
  Save,
  FileText,
  ShieldCheck,
  DollarSign,
  X,
  Check,
  Ban,
  Inbox,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const dollars = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const emptyTerms = {
  sub_cycle_1_pct: 50,
  sub_cycle_2_pct: 30,
  sub_cycle_3plus_pct: 20,
  lulu_profit_pct: 40,
  ebook_pct: 40,
  monthly_sub_minimum: 5,
  refund_clawback_days: 7,
};

export default function AdminAffiliates() {
  const [mode, setMode] = useState("peers"); // "peers" | "partners"

  return (
    <div className="min-h-screen">
      <header className="px-6 md:px-12 py-6 flex items-center justify-between border-b border-obsidian-600">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-bone-100"
        >
          <ChevronLeft className="h-3 w-3" /> Admin
        </Link>
        <div className="flex items-center gap-4">
          <AdminToggle />
          <UserBadge />
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 md:px-12 pt-12 pb-24">
        <div className="flex items-center gap-3 mb-4">
          <Handshake className="h-5 w-5 text-terracotta" />
          <p className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta">
            Affiliates
          </p>
        </div>
        <h1 className="font-serif text-4xl text-bone-100 mb-2">Word of mouth</h1>
        <p className="font-serif italic text-bone-400 mb-8 max-w-2xl">
          Two programs share this page — peer referrals (free months for every 3
          paid conversions) and paid partners (cash commissions with a vanity
          code and signed agreement).
        </p>

        {/* Toggle */}
        <div
          className="inline-flex border border-obsidian-600 mb-10"
          data-testid="affiliate-mode-toggle"
        >
          {[
            { id: "peers", label: "Peer referrals" },
            { id: "partners", label: "Paid partners" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setMode(t.id)}
              data-testid={`affiliate-mode-${t.id}`}
              className={`px-6 py-3 font-sans text-xs uppercase tracking-[0.24em] transition-colors ${
                mode === t.id
                  ? "bg-terracotta text-obsidian-900"
                  : "text-bone-400 hover:text-bone-100"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {mode === "peers" ? <PeerRefsPanel /> : <PartnersPanel />}
      </div>
    </div>
  );
}

// ---------- Peer referrals (existing "Path B" free-months) ----------

function PeerRefsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setData(await fetchAdminAffiliates());
      } catch {
        toast.error("Could not load peer referrers.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading || !data) {
    return (
      <div className="flex items-center gap-3 text-bone-400">
        <Loader2 className="h-4 w-4 animate-spin" /> loading
      </div>
    );
  }

  return (
    <>
      <p className="font-sans text-sm text-bone-400 mb-6">
        Every 3 paid subscribers a user brings = 1 free month.
      </p>
      <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-10" data-testid="affiliate-stats">
        <div className="border border-obsidian-600 p-4 sm:p-5">
          <p className="font-sans text-[0.55rem] sm:text-[0.65rem] uppercase tracking-[0.22em] sm:tracking-[0.24em] text-bone-500 mb-2">
            Total referrers
          </p>
          <p className="font-serif text-2xl sm:text-3xl text-bone-100">
            {data.total_referrers}
          </p>
        </div>
        <div className="border border-obsidian-600 p-4 sm:p-5">
          <p className="font-sans text-[0.55rem] sm:text-[0.65rem] uppercase tracking-[0.22em] sm:tracking-[0.24em] text-bone-500 mb-2">
            Paid conversions
          </p>
          <p className="font-serif text-2xl sm:text-3xl text-terracotta">
            {data.total_conversions}
          </p>
        </div>
        <div className="border border-obsidian-600 p-4 sm:p-5">
          <p className="font-sans text-[0.55rem] sm:text-[0.65rem] uppercase tracking-[0.22em] sm:tracking-[0.24em] text-bone-500 mb-2">
            Free months
          </p>
          <p className="font-serif text-2xl sm:text-3xl text-terracotta">
            {Math.floor(data.total_conversions / 3)}
          </p>
        </div>
      </div>
      <div data-testid="affiliate-list">
        {/* Desktop table */}
        <div className="hidden md:block border border-obsidian-600">
          <div className="grid grid-cols-6 gap-4 px-5 py-3 border-b border-obsidian-600 font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500">
            <div className="col-span-2">Referrer</div>
            <div>Code</div>
            <div className="text-right">Signups</div>
            <div className="text-right">Paid</div>
            <div className="text-right">Months</div>
          </div>
          {data.affiliates.length === 0 && (
            <p className="font-sans text-bone-500 text-sm p-6">
              No peer referrers yet.
            </p>
          )}
          {data.affiliates.map((a) => (
            <div
              key={a.user_id}
              data-testid={`affiliate-row-${a.affiliate_code}`}
              className="grid grid-cols-6 gap-4 px-5 py-4 border-b border-obsidian-600 last:border-b-0 items-center"
            >
              <div className="col-span-2">
                <p className="font-serif text-lg text-bone-100 truncate">
                  {a.name || "—"}
                </p>
                <p className="font-sans text-xs text-bone-500 truncate">{a.email}</p>
              </div>
              <div>
                <code className="text-terracotta font-mono text-sm">
                  {a.affiliate_code}
                </code>
              </div>
              <div className="text-right font-serif text-lg text-bone-100">
                {a.referrals}
              </div>
              <div className="text-right font-serif text-lg text-terracotta">
                {a.conversions}
              </div>
              <div className="text-right font-serif text-lg text-bone-100">
                {a.months_earned}
                <span className="text-bone-500 text-sm"> / {a.months_applied} applied</span>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile card stack */}
        <div className="md:hidden space-y-3">
          {data.affiliates.length === 0 && (
            <p className="font-sans text-bone-500 text-sm p-5 border border-obsidian-600">
              No peer referrers yet.
            </p>
          )}
          {data.affiliates.map((a) => (
            <div
              key={a.user_id}
              data-testid={`affiliate-row-${a.affiliate_code}-mobile`}
              className="border border-obsidian-600 bg-obsidian-800/30 p-4"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="min-w-0 flex-1 mr-3">
                  <p className="font-serif text-lg text-bone-100 truncate">
                    {a.name || "—"}
                  </p>
                  <p className="font-sans text-xs text-bone-500 truncate">{a.email}</p>
                </div>
                <code className="text-terracotta font-mono text-sm shrink-0">
                  {a.affiliate_code}
                </code>
              </div>
              <div className="grid grid-cols-3 gap-2 pt-2 border-t border-obsidian-600/60 text-center">
                <div>
                  <p className="font-sans text-[0.6rem] uppercase tracking-[0.22em] text-bone-500">Signups</p>
                  <p className="font-serif text-lg text-bone-100">{a.referrals}</p>
                </div>
                <div>
                  <p className="font-sans text-[0.6rem] uppercase tracking-[0.22em] text-terracotta">Paid</p>
                  <p className="font-serif text-lg text-terracotta">{a.conversions}</p>
                </div>
                <div>
                  <p className="font-sans text-[0.6rem] uppercase tracking-[0.22em] text-bone-500">Months</p>
                  <p className="font-serif text-lg text-bone-100">
                    {a.months_earned}
                    <span className="text-bone-500 text-xs">/{a.months_applied}</span>
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// ---------- Paid partners (Path A — cash commissions) ----------

function PartnersPanel() {
  const [list, setList] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [showPromote, setShowPromote] = useState(false);
  const [audit, setAudit] = useState(null);
  const [busy, setBusy] = useState(false);

  const refreshList = async () => {
    try {
      setLoadingList(true);
      setList(await fetchAdminPartners());
    } catch {
      toast.error("Could not load partners");
    } finally {
      setLoadingList(false);
    }
  };
  useEffect(() => {
    refreshList();
  }, []);

  const openDetail = async (userId) => {
    setSelected(userId);
    setAudit(null);
    try {
      setLoadingDetail(true);
      setDetail(await fetchAdminPartnerDetail(userId));
    } catch {
      toast.error("Could not load partner");
    } finally {
      setLoadingDetail(false);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <p className="font-sans text-sm text-bone-400 max-w-xl">
          Vanity codes, custom commission ladder (50/30/20 default), agreements,
          Stripe reconciliation, manual payouts.
        </p>
        <Button
          onClick={() => setShowPromote(true)}
          data-testid="admin-promote-partner-btn"
          className="rounded-none bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wider text-xs px-6 py-5"
        >
          <Plus className="h-4 w-4 mr-2" /> Promote a partner
        </Button>
      </div>

      {loadingList || !list ? (
        <div className="flex items-center gap-3 text-bone-400">
          <Loader2 className="h-4 w-4 animate-spin" /> loading
        </div>
      ) : (
        <>
          <PayoutRequestsPanel onChange={refreshList} />
          <PartnerList list={list} onOpen={openDetail} />
        </>
      )}

      {selected && (
        <PartnerDrawer
          userId={selected}
          detail={detail}
          loading={loadingDetail}
          busy={busy}
          setBusy={setBusy}
          audit={audit}
          onAudit={async (month) => {
            try {
              setAudit(await runPartnerAudit(selected, month));
            } catch {
              toast.error("Audit failed");
            }
          }}
          onReload={() => openDetail(selected)}
          onClose={() => {
            setSelected(null);
            setDetail(null);
            setAudit(null);
          }}
          onListRefresh={refreshList}
        />
      )}

      {showPromote && (
        <PromoteModal
          onClose={() => setShowPromote(false)}
          onSuccess={async (userId) => {
            setShowPromote(false);
            await refreshList();
            await openDetail(userId);
          }}
        />
      )}
    </>
  );
}

function PayoutRequestsPanel({ onChange }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const r = await fetchAdminPayoutRequests("pending");
      setRequests(r.requests || []);
    } catch {
      toast.error("Could not load payout requests");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    if (!window.confirm("Mark this payout as paid? This finalises the ledger.")) return;
    try {
      setBusyId(id);
      const r = await approvePayoutRequest(id);
      const amount = ((r?.payout?.total_cents) || 0) / 100;
      toast.success(`Paid $${amount.toFixed(2)} — commissions marked paid.`);
      await load();
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (id) => {
    if (!window.confirm("Reject this payout request? The partner can request again.")) return;
    try {
      setBusyId(id);
      await rejectPayoutRequest(id);
      toast.success("Request rejected.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="border border-obsidian-600 p-5 mb-6 flex items-center gap-3 text-bone-400">
        <Loader2 className="h-4 w-4 animate-spin" /> loading payout requests
      </div>
    );
  }
  if (!requests.length) {
    return (
      <div
        className="border border-obsidian-600 bg-obsidian-800/40 p-5 mb-6 flex items-center gap-3"
        data-testid="admin-payout-requests-empty"
      >
        <Inbox className="h-4 w-4 text-bone-500" />
        <p className="font-sans text-sm text-bone-500">
          No pending payout requests — inbox zero.
        </p>
      </div>
    );
  }

  return (
    <div
      className="border border-terracotta bg-terracotta/5 p-5 mb-8"
      data-testid="admin-payout-requests"
    >
      <div className="flex items-center gap-2 mb-4">
        <Inbox className="h-4 w-4 text-terracotta" />
        <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta">
          {requests.length} pending payout {requests.length === 1 ? "request" : "requests"}
        </p>
      </div>
      <div className="space-y-3">
        {requests.map((r) => (
          <div
            key={r.id}
            data-testid={`admin-payout-request-${r.id}`}
            className="border border-obsidian-600 bg-obsidian-900 p-4 grid grid-cols-1 md:grid-cols-[1fr,auto] gap-3 items-center"
          >
            <div className="min-w-0">
              <p className="font-serif text-lg text-bone-100 truncate">
                {r.partner_name || r.partner_email || r.partner_user_id}{" "}
                <code className="ml-2 text-terracotta font-mono text-sm">{r.partner_code}</code>
              </p>
              <p className="font-sans text-xs text-bone-500 mt-0.5">
                Requested {new Date(r.requested_at).toLocaleString()} · via{" "}
                <span className="text-bone-300">{r.method}</span> · handle{" "}
                <span className="font-mono text-bone-300">{r.handle}</span>
              </p>
              {r.notes && (
                <p className="font-sans italic text-xs text-bone-400 mt-1">"{r.notes}"</p>
              )}
            </div>
            <div className="flex items-center gap-3 flex-wrap justify-end">
              <p
                className="font-serif text-2xl text-terracotta whitespace-nowrap"
                data-testid={`admin-payout-amount-${r.id}`}
              >
                {dollars(r.amount_cents)}
              </p>
              <Button
                onClick={() => approve(r.id)}
                disabled={busyId === r.id}
                data-testid={`admin-payout-approve-${r.id}`}
                className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-bone-100 font-sans uppercase tracking-wider text-xs px-4 py-3 min-h-[44px]"
              >
                {busyId === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1" /> Mark paid</>}
              </Button>
              <Button
                onClick={() => reject(r.id)}
                disabled={busyId === r.id}
                data-testid={`admin-payout-reject-${r.id}`}
                variant="outline"
                className="rounded-none border-bone-500 bg-transparent text-bone-300 hover:text-red-400 hover:border-red-400 hover:bg-transparent font-sans uppercase tracking-wider text-xs px-3 py-3 min-h-[44px]"
              >
                <Ban className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PartnerList({ list, onOpen }) {
  if (!list.partners.length) {
    return (
      <p className="font-sans text-bone-500 text-sm p-6 border border-obsidian-600">
        No partners yet — promote your first influencer above.
      </p>
    );
  }
  return (
    <div data-testid="admin-partner-list">
      {/* Desktop table */}
      <div className="hidden md:block border border-obsidian-600">
        <div className="grid grid-cols-6 gap-4 px-5 py-3 border-b border-obsidian-600 font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500">
          <div className="col-span-2">Partner</div>
          <div>Code</div>
          <div className="text-right">Qualified $</div>
          <div className="text-right">Paid $</div>
          <div className="text-right">Status</div>
        </div>
        {list.partners.map((p) => {
          const q = p.totals?.qualified?.cents || 0;
          const paid = p.totals?.paid?.cents || 0;
          return (
            <button
              key={p.user_id}
              onClick={() => onOpen(p.user_id)}
              data-testid={`admin-partner-row-${p.code}`}
              className="w-full grid grid-cols-6 gap-4 px-5 py-4 border-b border-obsidian-600 last:border-b-0 items-center text-left hover:bg-obsidian-800/40 transition-colors"
            >
              <div className="col-span-2">
                <p className="font-serif text-lg text-bone-100 truncate">
                  {p.name || "—"}
                </p>
                <p className="font-sans text-xs text-bone-500 truncate">{p.email}</p>
              </div>
              <div>
                <code className="text-terracotta font-mono text-sm">{p.code}</code>
              </div>
              <div className="text-right font-serif text-lg text-terracotta">
                {dollars(q)}
              </div>
              <div className="text-right font-serif text-lg text-emerald-400">
                {dollars(paid)}
              </div>
              <div className="text-right font-sans text-xs">
                <span className={p.active ? "text-emerald-400" : "text-bone-500"}>
                  {p.active ? "Active" : "Deactivated"}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Mobile card stack */}
      <div className="md:hidden space-y-3">
        {list.partners.map((p) => {
          const q = p.totals?.qualified?.cents || 0;
          const paid = p.totals?.paid?.cents || 0;
          return (
            <button
              key={p.user_id}
              onClick={() => onOpen(p.user_id)}
              data-testid={`admin-partner-row-${p.code}-mobile`}
              className="w-full border border-obsidian-600 bg-obsidian-800/30 p-4 text-left hover:border-terracotta transition-colors"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="min-w-0 flex-1 mr-3">
                  <p className="font-serif text-lg text-bone-100 truncate">
                    {p.name || "—"}
                  </p>
                  <p className="font-sans text-xs text-bone-500 truncate">{p.email}</p>
                </div>
                <code className="text-terracotta font-mono text-sm shrink-0">{p.code}</code>
              </div>
              <div className="flex items-end justify-between pt-2 border-t border-obsidian-600/60">
                <div>
                  <p className="font-sans text-[0.6rem] uppercase tracking-[0.22em] text-terracotta">Qualified</p>
                  <p className="font-serif text-lg text-terracotta">{dollars(q)}</p>
                </div>
                <div>
                  <p className="font-sans text-[0.6rem] uppercase tracking-[0.22em] text-emerald-400">Paid</p>
                  <p className="font-serif text-lg text-emerald-400">{dollars(paid)}</p>
                </div>
                <span className={`font-sans text-[0.65rem] uppercase tracking-[0.22em] ${p.active ? "text-emerald-400" : "text-bone-500"}`}>
                  {p.active ? "Active" : "Off"}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PromoteModal({ onClose, onSuccess }) {
  const [userId, setUserId] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);   // the picked user row
  const [emailQuery, setEmailQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [code, setCode] = useState("");
  const [terms, setTerms] = useState({ ...emptyTerms });
  const [busy, setBusy] = useState(false);

  // Debounced type-ahead. Empty query resets the list; <2 chars is a no-op.
  useEffect(() => {
    if (selectedUser) return;      // pause search once a row is picked
    const q = emailQuery.trim();
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        setSearching(true);
        const r = await adminSearchUsers(q, 15);
        setSuggestions(r.users || []);
      } catch {
        setSuggestions([]);
      } finally {
        setSearching(false);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [emailQuery, selectedUser]);

  const pickUser = (u) => {
    setSelectedUser(u);
    setUserId(u.user_id);
    setEmailQuery(u.email || u.name || u.user_id);
    setSuggestions([]);
  };
  const clearPick = () => {
    setSelectedUser(null);
    setUserId("");
    setEmailQuery("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!userId) {
      toast.error("Pick a user first.");
      return;
    }
    try {
      setBusy(true);
      const r = await promotePartner({
        user_id: userId.trim(),
        code: code.trim().toUpperCase(),
        terms,
      });
      toast.success("Partner promoted");
      onSuccess(r.partner.user_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Promote failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-obsidian-950/85 backdrop-blur-md flex items-center justify-center p-4"
      data-testid="admin-promote-modal"
    >
      <div className="relative bg-obsidian-800 border border-obsidian-600 max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8">
        <button
          onClick={onClose}
          data-testid="admin-promote-close"
          className="absolute top-5 right-5 text-bone-500 hover:text-bone-200"
        >
          <X className="h-5 w-5" />
        </button>
        <h2 className="font-serif text-2xl text-bone-100 mb-1">
          Promote user to partner
        </h2>
        <p className="font-sans text-xs text-bone-500 mb-6">
          Search by email or name — no user IDs to memorise.
        </p>
        <form onSubmit={submit} className="space-y-5">
          <div className="relative">
            <label className="font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500 block mb-2">
              Find user
            </label>
            {selectedUser ? (
              <div
                className="flex items-center gap-3 border border-terracotta bg-terracotta/5 px-4 py-3"
                data-testid="admin-promote-picked-user"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-serif text-base text-bone-100 truncate">
                    {selectedUser.name || "(no name)"}{" "}
                    <span className="text-bone-400">· {selectedUser.email}</span>
                  </p>
                  <p className="font-mono text-[0.65rem] text-bone-500 mt-0.5">
                    {selectedUser.user_id}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={clearPick}
                  data-testid="admin-promote-clear-user"
                  className="font-sans uppercase tracking-[0.2em] text-[0.6rem] text-bone-400 hover:text-terracotta transition-colors"
                >
                  Change
                </button>
              </div>
            ) : (
              <>
                <input
                  value={emailQuery}
                  onChange={(e) => setEmailQuery(e.target.value)}
                  data-testid="admin-promote-email-search"
                  placeholder="type an email or name…"
                  autoComplete="off"
                  className="w-full bg-obsidian-900 border border-obsidian-600 focus:border-terracotta text-bone-100 px-4 py-3 font-sans text-sm focus:outline-none"
                />
                {(searching || suggestions.length > 0 || emailQuery.trim().length >= 2) && (
                  <div
                    className="absolute left-0 right-0 top-full mt-1 z-10 max-h-64 overflow-y-auto border border-obsidian-600 bg-obsidian-900 shadow-xl"
                    data-testid="admin-promote-suggestions"
                  >
                    {searching ? (
                      <p className="p-3 font-sans text-xs text-bone-500 flex items-center gap-2">
                        <Loader2 className="h-3 w-3 animate-spin" /> searching…
                      </p>
                    ) : suggestions.length === 0 ? (
                      <p className="p-3 font-sans text-xs text-bone-500">
                        No matches for "{emailQuery.trim()}".
                      </p>
                    ) : (
                      suggestions.map((u) => (
                        <button
                          type="button"
                          key={u.user_id}
                          onClick={() => pickUser(u)}
                          disabled={u.is_partner}
                          data-testid={`admin-promote-suggest-${u.user_id}`}
                          className="w-full text-left px-4 py-2.5 border-b border-obsidian-600 last:border-0 hover:bg-obsidian-800 disabled:bg-obsidian-800/40 disabled:cursor-not-allowed transition-colors"
                        >
                          <p className="font-serif text-sm text-bone-100 truncate">
                            {u.name || "(no name)"}{" "}
                            <span className="text-bone-400">· {u.email}</span>
                            {u.is_partner && (
                              <span className="ml-2 font-sans uppercase tracking-[0.2em] text-[0.55rem] text-terracotta">
                                already partner
                              </span>
                            )}
                          </p>
                          <p className="font-mono text-[0.6rem] text-bone-500 truncate">
                            {u.user_id}
                          </p>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          <div>
            <label className="font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500 block mb-2">
              Vanity code
            </label>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              required
              minLength={3}
              maxLength={32}
              data-testid="admin-promote-code"
              placeholder="KARLA20"
              className="w-full bg-obsidian-900 border border-obsidian-600 focus:border-terracotta text-bone-100 px-4 py-3 font-mono text-sm uppercase focus:outline-none"
            />
          </div>
          <TermsEditor terms={terms} onChange={setTerms} />
          <Button
            type="submit"
            disabled={busy || !selectedUser}
            data-testid="admin-promote-submit"
            className="rounded-none bg-terracotta hover:bg-terracotta-deep disabled:bg-obsidian-700 disabled:text-bone-500 text-bone-100 font-sans uppercase tracking-wider text-xs py-5 px-8 w-full"
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            Promote partner
          </Button>
        </form>
      </div>
    </div>
  );
}

function TermsEditor({ terms, onChange }) {
  const fields = [
    ["sub_cycle_1_pct", "Sub · cycle 1 %"],
    ["sub_cycle_2_pct", "Sub · cycle 2 %"],
    ["sub_cycle_3plus_pct", "Sub · cycle 3+ %"],
    ["lulu_profit_pct", "Lulu profit %"],
    ["ebook_pct", "Ebook %"],
    ["monthly_sub_minimum", "Monthly sub minimum"],
    ["refund_clawback_days", "Refund clawback (days)"],
  ];
  return (
    <div className="border border-obsidian-600 p-4">
      <p className="font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500 mb-4">
        Commission structure
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {fields.map(([k, label]) => (
          <div key={k}>
            <label className="font-sans text-[0.6rem] uppercase tracking-[0.22em] text-bone-500 block mb-1">
              {label}
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={terms[k] ?? ""}
              onChange={(e) =>
                onChange({ ...terms, [k]: Number(e.target.value) || 0 })
              }
              data-testid={`terms-${k}`}
              className="w-full bg-obsidian-900 border border-obsidian-600 focus:border-terracotta text-bone-100 px-3 py-2 font-mono text-sm focus:outline-none"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function PartnerDrawer({
  userId,
  detail,
  loading,
  busy,
  setBusy,
  audit,
  onAudit,
  onReload,
  onClose,
  onListRefresh,
}) {
  const [termsDraft, setTermsDraft] = useState(null);
  const [payoutMethod, setPayoutMethod] = useState("venmo");
  const [payoutNotes, setPayoutNotes] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [agreementHtml, setAgreementHtml] = useState(null);

  useEffect(() => {
    if (detail?.partner) {
      setTermsDraft({ ...emptyTerms, ...(detail.partner.terms || {}) });
      setSelectedIds([]);
      setAgreementHtml(null);
    }
  }, [detail]);

  if (!detail || loading) {
    return (
      <div className="fixed inset-0 z-40 bg-obsidian-950/85 backdrop-blur-md flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-bone-400" />
      </div>
    );
  }

  const { partner, commissions = [], payouts = [] } = detail;
  const qualifiedRows = commissions.filter((c) => c.status === "qualified");
  const totalSelected = commissions
    .filter((c) => selectedIds.includes(c.id))
    .reduce((s, c) => s + c.commission_cents, 0);

  const saveTerms = async () => {
    try {
      setBusy(true);
      await updatePartnerTerms(userId, termsDraft);
      toast.success("Terms saved");
      await onReload();
      await onListRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const genAgreement = async () => {
    try {
      setBusy(true);
      const r = await generatePartnerAgreement(userId);
      setAgreementHtml(r.html);
      toast.success("Agreement generated — partner can now accept");
      await onReload();
    } catch {
      toast.error("Generate failed");
    } finally {
      setBusy(false);
    }
  };

  const recordPayout = async () => {
    if (!selectedIds.length) {
      toast.error("Select at least one qualified commission");
      return;
    }
    try {
      setBusy(true);
      await recordPartnerPayout(userId, {
        commission_ids: selectedIds,
        method: payoutMethod,
        notes: payoutNotes || undefined,
      });
      toast.success(`Payout recorded — ${dollars(totalSelected)} marked paid`);
      setSelectedIds([]);
      setPayoutNotes("");
      await onReload();
      await onListRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payout failed");
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async () => {
    if (
      !confirm(
        `Deactivate ${partner.code}? They keep past commissions but stop earning new ones.`
      )
    )
      return;
    try {
      setBusy(true);
      await deactivatePartner(userId);
      toast.success("Partner deactivated");
      onListRefresh();
      onClose();
    } catch {
      toast.error("Deactivate failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 bg-obsidian-950/85 backdrop-blur-md overflow-y-auto"
      data-testid="admin-partner-drawer"
    >
      <div className="max-w-4xl mx-auto my-8 bg-obsidian-800 border border-obsidian-600 p-8 md:p-10">
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="font-mono text-2xl text-terracotta mb-1">
              {partner.code}
            </p>
            <h2 className="font-serif text-2xl text-bone-100">
              {partner.name || "—"}
            </h2>
            <p className="font-sans text-xs text-bone-500">{partner.email}</p>
          </div>
          <button
            onClick={onClose}
            data-testid="admin-partner-close"
            className="text-bone-500 hover:text-bone-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {termsDraft && (
          <>
            <TermsEditor terms={termsDraft} onChange={setTermsDraft} />
            <div className="mt-3 mb-8 flex items-center gap-3 flex-wrap">
              <Button
                onClick={saveTerms}
                disabled={busy}
                data-testid="admin-partner-save-terms"
                variant="outline"
                className="rounded-none border-bone-500 bg-transparent text-bone-100 hover:text-terracotta hover:border-terracotta hover:bg-transparent font-sans uppercase tracking-wider text-xs"
              >
                <Save className="h-4 w-4 mr-2" /> Save terms
              </Button>
              <Button
                onClick={genAgreement}
                disabled={busy}
                data-testid="admin-partner-gen-agreement"
                variant="outline"
                className="rounded-none border-bone-500 bg-transparent text-bone-100 hover:text-terracotta hover:border-terracotta hover:bg-transparent font-sans uppercase tracking-wider text-xs"
              >
                <FileText className="h-4 w-4 mr-2" /> Generate agreement
              </Button>
              <Button
                onClick={() => onAudit(null)}
                disabled={busy}
                data-testid="admin-partner-run-audit"
                variant="outline"
                className="rounded-none border-bone-500 bg-transparent text-bone-100 hover:text-terracotta hover:border-terracotta hover:bg-transparent font-sans uppercase tracking-wider text-xs"
              >
                <ShieldCheck className="h-4 w-4 mr-2" /> Run Stripe audit
              </Button>
              {partner.active && (
                <Button
                  onClick={deactivate}
                  disabled={busy}
                  data-testid="admin-partner-deactivate"
                  variant="outline"
                  className="rounded-none border-red-500/40 bg-transparent text-red-400 hover:text-red-300 hover:border-red-400 hover:bg-transparent font-sans uppercase tracking-wider text-xs ml-auto"
                >
                  Deactivate
                </Button>
              )}
            </div>
          </>
        )}

        {agreementHtml && (
          <div className="mb-8 border border-obsidian-600 p-6" data-testid="admin-agreement-preview">
            <p className="font-sans text-[0.65rem] uppercase tracking-[0.24em] text-terracotta mb-3">
              Agreement preview · partner will see this
            </p>
            <div
              className="max-h-72 overflow-y-auto font-sans text-sm text-bone-300 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: agreementHtml }}
            />
          </div>
        )}

        {audit && (
          <div className="mb-8 border border-obsidian-600 p-6" data-testid="admin-audit-result">
            <p className="font-sans text-[0.65rem] uppercase tracking-[0.24em] text-terracotta mb-3">
              Stripe reconciliation · {audit.month_key}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <div>
                <p className="font-sans text-[0.6rem] uppercase text-bone-500">Pending</p>
                <p className="font-serif text-lg text-bone-300">
                  {dollars(audit.totals.pending_cents)}
                </p>
              </div>
              <div>
                <p className="font-sans text-[0.6rem] uppercase text-bone-500">Qualified</p>
                <p className="font-serif text-lg text-terracotta">
                  {dollars(audit.totals.qualified_cents)}
                </p>
              </div>
              <div>
                <p className="font-sans text-[0.6rem] uppercase text-bone-500">Paid</p>
                <p className="font-serif text-lg text-emerald-400">
                  {dollars(audit.totals.paid_cents)}
                </p>
              </div>
              <div>
                <p className="font-sans text-[0.6rem] uppercase text-bone-500">Rows</p>
                <p className="font-serif text-lg text-bone-100">{audit.totals.row_count}</p>
              </div>
            </div>
            {audit.clean ? (
              <p className="font-sans text-sm text-emerald-400" data-testid="admin-audit-clean">
                ✓ No mismatches vs Stripe. Safe to pay out.
              </p>
            ) : (
              <div data-testid="admin-audit-mismatches">
                <p className="font-sans text-sm text-red-400 mb-2">
                  {audit.mismatches.length} mismatch
                  {audit.mismatches.length === 1 ? "" : "es"} — resolve before paying out.
                </p>
                <ul className="font-mono text-xs text-bone-300 space-y-1 max-h-40 overflow-y-auto">
                  {audit.mismatches.map((m, i) => (
                    <li key={i}>
                      [{m.type}] commission={m.commission_id} charge={m.charge_id}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <p className="font-sans text-[0.65rem] uppercase tracking-[0.24em] text-terracotta mb-3">
          Commissions ledger
        </p>
        <div className="border border-obsidian-600 mb-4 overflow-x-auto" data-testid="admin-commission-table">
          <div className="min-w-[640px]">
          <div className="grid grid-cols-6 gap-3 px-4 py-3 border-b border-obsidian-600 font-sans text-[0.6rem] uppercase tracking-[0.22em] text-bone-500">
            <div>Select</div>
            <div>Date</div>
            <div>Source · cycle</div>
            <div className="text-right">Gross</div>
            <div className="text-right">Rate · $</div>
            <div className="text-right">Status</div>
          </div>
          {commissions.length === 0 && (
            <p className="font-sans text-xs text-bone-500 p-4">No commissions yet.</p>
          )}
          {commissions.map((c) => {
            const isQualified = c.status === "qualified";
            return (
              <div
                key={c.id}
                data-testid={`admin-commission-${c.id}`}
                className="grid grid-cols-6 gap-3 px-4 py-3 border-b border-obsidian-600 last:border-b-0 items-center"
              >
                <div>
                  {isQualified && (
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(c.id)}
                      onChange={(e) =>
                        setSelectedIds(
                          e.target.checked
                            ? [...selectedIds, c.id]
                            : selectedIds.filter((x) => x !== c.id)
                        )
                      }
                      data-testid={`admin-commission-check-${c.id}`}
                    />
                  )}
                </div>
                <div className="font-sans text-xs text-bone-400">
                  {new Date(c.created_at).toLocaleDateString()}
                </div>
                <div className="font-sans text-xs text-bone-200">
                  {c.source_type} {c.billing_cycle ? `· #${c.billing_cycle}` : ""}
                </div>
                <div className="text-right font-mono text-xs text-bone-300">
                  {dollars(c.gross_cents)}
                </div>
                <div className="text-right font-mono text-xs text-bone-100">
                  {c.rate_pct}% · {dollars(c.commission_cents)}
                </div>
                <div className="text-right font-sans text-[0.65rem] uppercase tracking-[0.22em]">
                  <span
                    className={
                      c.status === "paid"
                        ? "text-emerald-400"
                        : c.status === "qualified"
                        ? "text-terracotta"
                        : c.status === "voided"
                        ? "text-red-400"
                        : "text-bone-500"
                    }
                  >
                    {c.status}
                  </span>
                </div>
              </div>
            );
          })}
          </div>
        </div>

        {qualifiedRows.length > 0 && (
          <div className="border border-terracotta bg-terracotta/5 p-5" data-testid="admin-payout-recorder">
            <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta mb-3">
              Record a payout
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
              <div>
                <label className="font-sans text-[0.6rem] uppercase tracking-[0.22em] text-bone-500 block mb-1">
                  Method
                </label>
                <select
                  value={payoutMethod}
                  onChange={(e) => setPayoutMethod(e.target.value)}
                  data-testid="admin-payout-method"
                  className="bg-obsidian-900 border border-obsidian-600 text-bone-100 px-3 py-2 font-sans text-sm focus:outline-none"
                >
                  <option value="venmo">Venmo</option>
                  <option value="paypal">PayPal</option>
                  <option value="stripe_transfer">Stripe transfer</option>
                  <option value="ach">ACH</option>
                  <option value="check">Check</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="flex-1 w-full">
                <label className="font-sans text-[0.6rem] uppercase tracking-[0.22em] text-bone-500 block mb-1">
                  Notes (optional)
                </label>
                <input
                  value={payoutNotes}
                  onChange={(e) => setPayoutNotes(e.target.value)}
                  data-testid="admin-payout-notes"
                  placeholder="Feb payout · Venmo @karla"
                  className="w-full bg-obsidian-900 border border-obsidian-600 focus:border-terracotta text-bone-100 px-3 py-2 font-sans text-sm focus:outline-none"
                />
              </div>
              <Button
                onClick={recordPayout}
                disabled={busy || !selectedIds.length}
                data-testid="admin-payout-submit"
                className="rounded-none bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wider text-xs px-5 py-3"
              >
                <DollarSign className="h-4 w-4 mr-1" />
                Mark paid · {dollars(totalSelected)}
              </Button>
            </div>
          </div>
        )}

        {payouts.length > 0 && (
          <div className="mt-6" data-testid="admin-payouts-history">
            <p className="font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500 mb-2">
              Payouts history
            </p>
            <div className="border border-obsidian-600">
              {payouts.map((p) => (
                <div
                  key={p.id}
                  className="grid grid-cols-4 gap-3 px-4 py-3 border-b border-obsidian-600 last:border-b-0 items-center"
                >
                  <div className="font-sans text-xs text-bone-400">
                    {new Date(p.paid_at).toLocaleDateString()}
                  </div>
                  <div className="font-sans text-xs text-bone-200">{p.method}</div>
                  <div className="font-mono text-xs text-emerald-400">
                    {dollars(p.total_cents)}
                  </div>
                  <div
                    className="font-sans text-xs text-bone-400 truncate"
                    title={p.notes}
                  >
                    {p.notes || "—"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
